/**
 * Shared utilities for scoring modules
 */

import { logger } from '../logger';

/**
 * Normalize email address for consistent comparison
 * Handles "Name <email>" format and lowercases
 */
export function normalizeEmail(email: string): string {
  if (email.includes('<')) {
    const match = email.match(/<([^>]+)>/);
    return match ? match[1].toLowerCase().trim() : email.toLowerCase().trim();
  }
  return email.toLowerCase().trim();
}

/**
 * Check if subject line suggests a reply or forward
 */
export function isReplyOrForward(subject: string): boolean {
  if (!subject) return false;
  const normalized = subject.trim();
  const lower = normalized.toLowerCase();
  return lower.startsWith('re:') || lower.startsWith('fwd:') || lower.startsWith('fw:');
}

/**
 * Extract reply/forward prefix from subject
 */
export function getReplyPrefix(subject: string): 'Re' | 'Fwd' | null {
  if (!subject) return null;
  const normalized = subject.trim();
  const lower = normalized.toLowerCase();
  if (lower.startsWith('re:')) return 'Re';
  if (lower.startsWith('fwd:') || lower.startsWith('fw:')) return 'Fwd';
  return null;
}

/**
 * Check if text contains fake quoted text patterns
 */
export function containsFakeQuotedText(text: string): {
  detected: boolean;
  patterns: string[];
} {
  if (!text) return { detected: false, patterns: [] };

  const patterns: string[] = [];
  const lowerText = text.toLowerCase();

  // Pattern: "On [date] [person] wrote:"
  const onDatePattern = /on\s+\w+,\s+\w+\s+\d+,\s+\d+\s+at\s+\d+.*wrote:/i;
  if (onDatePattern.test(text)) {
    patterns.push('on_date_wrote');
  }

  // Pattern: "-----Original Message-----"
  if (
    lowerText.includes('-----original message-----') ||
    lowerText.includes('---original message---')
  ) {
    patterns.push('original_message');
  }

  // Pattern: Quoted text with ">" markers (but check if it's substantial)
  const quotedLines = text.split('\n').filter((line) => line.trim().startsWith('>'));
  if (quotedLines.length >= 3) {
    patterns.push('quoted_lines');
  }

  // Pattern: "From: [email]" in body (suggests fake forward)
  if (
    /from:\s*[^\s@]+@[^\s@]+\.[^\s@]+/i.test(text) &&
    !text.includes('-----Original Message-----')
  ) {
    patterns.push('from_header_in_body');
  }

  return {
    detected: patterns.length > 0,
    patterns,
  };
}

/**
 * Log scoring decision with context
 */
export function logScoringDecision(context: {
  userId?: string;
  messageId?: string;
  senderEmail?: string;
  checkName: string;
  score: number;
  reason: string;
}): void {
  logger.debug('Scoring decision', {
    check: context.checkName,
    score: context.score,
    reason: context.reason,
    userId: context.userId,
    messageId: context.messageId,
    senderEmail: context.senderEmail,
  });
}

/**
 * Clamp score contribution to prevent extreme values
 */
export function clampScore(score: number, min: number = -5, max: number = 5): number {
  return Math.max(min, Math.min(max, score));
}
